<script lang="ts" setup>
import FieldText from '@/components/shared/Fields/FieldText.vue'
import Icon from '@/components/shared/Icon.vue'
import { getThemeSettings } from '@/lib'
import { useForm } from 'vee-validate'
import { watch, ref, computed, onMounted, onBeforeUnmount, nextTick } from 'vue'
import { useRoute } from 'vue-router'
import LayoutHeaderProfile from './LayoutHeaderProfile.vue'
import Notifications from '@/components/shared/Notifications/Notifications.vue'
import LayoutSavedSearchDropdown from './LayoutSavedSearchDropdown.vue'
import LayoutSiloDropdown from './LayoutSiloDropdown.vue'
import type { SavedSearch } from '@/types/shared.types'
import { useSearch } from '@/composables/useSearch'
import { useDebounceFn } from '@vueuse/core'
import { useSavedSearches } from '@/composables/useSavedSearches'
import { useCampaignPolicies } from '@/composables/useCampaignPolicies'

defineProps<{ visible: boolean }>()

defineEmits<{
  toggleSidebar: []
  search: [{ term: string; timestamp: number }]
}>()

const { showLogin } = getThemeSettings()

const route = useRoute()

// Use centralized silo filtering
const { updateSelectedSilos, selectedSilos, availableSilos, updateFilters } = useSearch()
const currentSilos = selectedSilos

const { handleSubmit, setValues, values } = useForm({
  initialValues: {
    search: '',
  },
})

const { savePassiveSearch, passiveSavedSearches, deleteSearchByItem, saveSearch } =
  useSavedSearches()

// Get campaign policies to check available notification channels
const { data: campaignPolicies } = useCampaignPolicies()

// Typeahead state (shows passive saved searches)
const isTypeaheadOpen = ref(false)
const typeaheadRoot = ref<HTMLElement | null>(null)
const activeTypeaheadIndex = ref(-1)

const filteredTypeahead = computed(() => {
  const term = values.search?.toString().trim().toLowerCase() || ''
  const items = passiveSavedSearches.value
  if (!term) return items
  return items.filter((s) => s.Term.toLowerCase().includes(term))
})

// Check if Service Panel Notifications channel is available
const isServicePanelNotificationsAvailable = computed(() => {
  if (!campaignPolicies.value?.AvailableNotificationChannels) return false
  return campaignPolicies.value.AvailableNotificationChannels.some(
    (channel) => channel.Name === 'Service Panel Notifications',
  )
})

function openTypeahead() {
  if (passiveSavedSearches.value.length > 0) {
    isTypeaheadOpen.value = true
    activeTypeaheadIndex.value = -1
  }
}
function closeTypeahead() {
  isTypeaheadOpen.value = false
  activeTypeaheadIndex.value = -1
}

function handleSelectTypeahead(search: SavedSearch) {
  setValues({ search: search.Term })
  updateSelectedSilos(search.Silos || [])
  closeTypeahead()
  onSubmit()
  focusSearchInput()
}

// Helper to get silo names from IDs (matches LayoutSavedSearchDropdown)
function getSiloNames(siloIds: string[] | null): string {
  if (!siloIds || siloIds.length === 0) return ''
  const names = siloIds
    .map((id) => availableSilos.value.find((silo) => silo.value === id)?.label || id)
    .slice(0, 3)
  if (siloIds.length > 3) return `${names.join(', ')} +${siloIds.length - 3} more`
  return names.join(', ')
}

async function handleDeleteTypeahead(search: SavedSearch, event: Event) {
  event.stopPropagation()
  await deleteSearchByItem(search)
}

async function handleSaveTypeahead(search: SavedSearch, event: Event) {
  event.stopPropagation()
  const name = search.Name || search.Term || 'Search'
  const term = search.Term || ''
  const silos = search.Silos || []
  await saveSearch(name, term, silos)
  // Remove passive duplicate if present
  if (search.Active === false) {
    await deleteSearchByItem(search)
  }
}

function handleDocumentClick(event: MouseEvent) {
  if (!typeaheadRoot.value) return
  const target = event.target as Node | null
  if (target && !typeaheadRoot.value.contains(target)) {
    closeTypeahead()
  }
}

function focusSearchInput() {
  nextTick(() => {
    const input = typeaheadRoot.value?.querySelector('input')
    input?.focus()
  })
}

function handleTypeaheadFocusOut(event: FocusEvent) {
  if (!typeaheadRoot.value) return
  const relatedTarget = event.relatedTarget as Node | null
  if (!relatedTarget || !typeaheadRoot.value.contains(relatedTarget)) {
    closeTypeahead()
  }
}

function focusTypeaheadOption(index: number) {
  nextTick(() => {
    const options = typeaheadRoot.value?.querySelectorAll<HTMLElement>('[data-typeahead-option]')
    if (!options || options.length === 0) return
    const normalizedIndex = ((index % options.length) + options.length) % options.length
    activeTypeaheadIndex.value = normalizedIndex
    const option = options[normalizedIndex]
    option?.focus()
    option?.scrollIntoView({ block: 'nearest' })
  })
}

function handleSearchKeydown(event: KeyboardEvent) {
  if (event.key === 'ArrowDown') {
    if (filteredTypeahead.value.length === 0) return
    if (!isTypeaheadOpen.value) openTypeahead()
    event.preventDefault()
    const nextIndex =
      activeTypeaheadIndex.value < filteredTypeahead.value.length - 1
        ? activeTypeaheadIndex.value + 1
        : 0
    focusTypeaheadOption(nextIndex)
  } else if (event.key === 'ArrowUp') {
    if (!isTypeaheadOpen.value || filteredTypeahead.value.length === 0) return
    event.preventDefault()
    const prevIndex =
      activeTypeaheadIndex.value > 0
        ? activeTypeaheadIndex.value - 1
        : filteredTypeahead.value.length - 1
    focusTypeaheadOption(prevIndex)
  } else if (event.key === 'Enter') {
    if (isTypeaheadOpen.value && activeTypeaheadIndex.value >= 0) {
      event.preventDefault()
      const selected = filteredTypeahead.value[activeTypeaheadIndex.value]
      if (selected) handleSelectTypeahead(selected)
    }
  } else if (event.key === 'Escape') {
    if (isTypeaheadOpen.value) {
      event.preventDefault()
      closeTypeahead()
    }
  } else if (event.key === 'Tab') {
    closeTypeahead()
  }
}

function handleTypeaheadOptionArrow(event: KeyboardEvent, indexDelta: number) {
  event.preventDefault()
  if (filteredTypeahead.value.length === 0) return
  focusTypeaheadOption(activeTypeaheadIndex.value + indexDelta)
}

watch(filteredTypeahead, (items) => {
  if (items.length === 0) {
    activeTypeaheadIndex.value = -1
  } else if (activeTypeaheadIndex.value >= items.length) {
    activeTypeaheadIndex.value = items.length - 1
  }
})

onMounted(() => {
  document.addEventListener('click', handleDocumentClick)
})
onBeforeUnmount(() => {
  document.removeEventListener('click', handleDocumentClick)
})

// Debounced submit to prevent rapid-fire searches
const debouncedSubmit = useDebounceFn((values: any) => {
  const searchTerm = values.search || ''
  updateFilters({ term: searchTerm })
  // Passive save whenever a search is made (term or silos)
  if (searchTerm || selectedSilos.value.length > 0) {
    savePassiveSearch(searchTerm, selectedSilos.value)
  }
}, 300)

const onSubmit = handleSubmit(debouncedSubmit)

function handleClear() {
  setValues({ search: '' })
  updateFilters({ term: '' })
}

function handleSiloUpdate(silos: string[]) {
  updateFilters({ silos })
  // Passive save on silo change
  const searchTerm = (values.search as string) || ''
  if (searchTerm || silos.length > 0) {
    savePassiveSearch(searchTerm, silos)
  }
}

function handleSelectSavedSearch(search: SavedSearch) {
  setValues({ search: search.Term })
  updateFilters({ term: search.Term, silos: search.Silos || [] })
  // Passive save to reflect user action
  savePassiveSearch(search.Term || '', search.Silos || [])
}

watch(
  () => route.query,
  () => {
    const queryParams = route.query
    setValues({ search: queryParams.term ? queryParams.term?.toString() : '' })
    // Close typeahead when route changes
    closeTypeahead()
  },
  { immediate: true },
)
</script>

<template>
  <header
    class="relative z-30 lg:z-auto bg-white dark:bg-gray-900 w-full h-[60px] shrink-0 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between gap-x-6 lg:gap-x-0 shadow dark:shadow-gray-900"
  >
    <!-- Toggle Button -->
    <div
      class="flex gap-x-4 items-center pl-4 sm:pl-6"
      :class="visible ? 'lg:w-[var(--sidebar-width)]' : 'w-auto'"
    >
      <button
        type="button"
        class="flex justify-center items-center w-7 h-7 rounded border border-transparent transition-colors default-focus hover:bg-gray-50 active:bg-gray-100 hover:border-gray-200 dark:hover:bg-gray-800 dark:active:bg-gray-700 dark:hover:border-gray-700"
        @click="$emit('toggleSidebar')"
      >
        <Icon variant="menu" class="w-6 h-6 text-gray-900 dark:text-gray-100" />
      </button>

    </div>
    <!-- Search Bar -->
    <div v-if="!showLogin" class="flex relative flex-1 gap-x-4 justify-center">
      <form class="flex gap-x-2.5 items-center w-full max-w-xl shrink-0 md:pr-0" @submit="onSubmit">
        <div
          ref="typeaheadRoot"
          class="relative flex-1"
          role="combobox"
          aria-haspopup="listbox"
          :aria-expanded="isTypeaheadOpen"
          aria-label="Search identities"
          :aria-controls="isTypeaheadOpen ? 'layout-header-typeahead-list' : undefined"
          :aria-owns="isTypeaheadOpen ? 'layout-header-typeahead-list' : undefined"
          @focusout="handleTypeaheadFocusOut"
        >
          <FieldText
            :show-clear="true"
            :show-label="false"
            :validate="false"
            name="search"
            placeholder="Search identities..."
            prefix-icon="search"
            type="text"
            class="flex-1"
            :stop-key-propagation="false"
            @clear="handleClear"
            @click="openTypeahead"
            @keyup="openTypeahead"
            @keydown="handleSearchKeydown"
          />
          <div
            v-if="isTypeaheadOpen && filteredTypeahead.length > 0"
            class="absolute z-40 mt-1 w-full bg-white rounded-lg border border-gray-300 shadow-lg dark:border-gray-700 dark:bg-gray-900"
          >
            <ul
              id="layout-header-typeahead-list"
              role="listbox"
              class="overflow-auto px-1 py-1 max-h-64 divide-y divide-gray-200 dark:divide-gray-700"
            >
              <li
                v-for="(item, idx) in filteredTypeahead"
                :key="`${item.Term}-${idx}`"
                :id="`layout-header-typeahead-option-${idx}`"
                data-typeahead-option
                class="group cursor-pointer py-2 px-2 text-[13px] text-gray-800 disabled:opacity-50 disabled:pointer-events-none dark:text-gray-300 select-none default-focus transition-colors"
                :class="{
                  'bg-gray-100 dark:bg-gray-800': idx === activeTypeaheadIndex,
                  'hover:bg-gray-100 dark:hover:bg-gray-800': idx !== activeTypeaheadIndex,
                }"
                role="option"
                :aria-selected="idx === activeTypeaheadIndex"
                tabindex="-1"
                @click="handleSelectTypeahead(item)"
                @keydown.enter.prevent="handleSelectTypeahead(item)"
                @keydown.space.prevent="handleSelectTypeahead(item)"
                @keydown.down.prevent="handleTypeaheadOptionArrow($event, 1)"
                @keydown.up.prevent="handleTypeaheadOptionArrow($event, -1)"
                @keydown.esc.prevent="
                  () => {
                    closeTypeahead()
                    focusSearchInput()
                  }
                "
                @focus="activeTypeaheadIndex = idx"
              >
                <div class="flex gap-x-3 w-full">
                  <div class="flex-1 min-w-0">
                    <div
                      class="mb-0.5 font-medium leading-tight text-gray-900 truncate dark:text-gray-100"
                    >
                      {{ item.Term || item.Name }}
                    </div>
                    <div class="flex gap-x-1 items-center text-xs text-gray-500 dark:text-gray-400">
                      <Icon variant="layers" class="size-3" />
                      <span class="max-w-[200px] truncate">{{ getSiloNames(item.Silos) }}</span>
                    </div>
                  </div>
                  <div class="flex items-center">
                    <button
                      type="button"
                      class="self-center p-1 rounded border border-transparent opacity-0 transition-all group-hover:opacity-100 group-focus-within:opacity-100 hover:bg-gray-50 hover:border-gray-200 dark:hover:bg-gray-800/20 dark:hover:border-gray-700 default-focus"
                      @click.stop="handleSaveTypeahead(item, $event)"
                    >
                      <Icon variant="bookmark" class="text-gray-600 size-3.5 dark:text-gray-400" />
                    </button>
                    <button
                      type="button"
                      class="self-center p-1 rounded border border-transparent opacity-0 transition-all group-hover:opacity-100 group-focus-within:opacity-100 hover:bg-error-50 hover:border-error-200 dark:hover:bg-error-900/20 dark:hover:border-error-800 default-focus"
                      @click.stop="handleDeleteTypeahead(item, $event)"
                    >
                      <Icon variant="trash" class="text-error-600 size-3.5 dark:text-error-400" />
                    </button>
                  </div>
                </div>
              </li>
            </ul>
          </div>
        </div>
        <LayoutSiloDropdown :selected-silos="currentSilos" @update-silos="handleSiloUpdate" />
        <LayoutSavedSearchDropdown
          :current-search-term="values.search"
          :current-silos="currentSilos"
          @select-search="handleSelectSavedSearch"
        />
      </form>
    </div>

    <!-- Dropdown -->
    <div class="flex gap-x-3 items-center pr-4 sm:pr-6">
      <Notifications v-if="isServicePanelNotificationsAvailable" />
      <LayoutHeaderProfile v-if="!showLogin" />
    </div>
  </header>
</template>
